import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Booking, BookingService } from 'us-web-services';
import {
  formatDateRange,
  getDurationHours,
  localDateTimeInputToApi,
  toLocalDateTimeInput,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const DEFAULT_AVATAR =
  'https://assets.urbansitter.com/static/img/us-default-avatar-320.png';

type StatusCode = 'sitter_confirmed' | 'cash' | 'sitter_cancel' | 'parent_cancel';

interface BookingWithExtras extends Booking {
  originalStart?: string;
  originalEnd?: string;
  message?: string;
}

const mapConfirmTypeToPayload = (
  confirmType: StatusCode,
): { statusCode: string; paymentType?: 'credit' | 'cash' } => {
  switch (confirmType) {
    case 'sitter_confirmed':
      return { statusCode: 'sitter_confirmed', paymentType: 'credit' };
    case 'cash':
      return { statusCode: 'sitter_confirmed', paymentType: 'cash' };
    case 'sitter_cancel':
      return { statusCode: 'sitter_cancel' };
    case 'parent_cancel':
      return { statusCode: 'parent_cancel' };
    default:
      return { statusCode: confirmType };
  }
};

export const BookingConfirmHours = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [booking, setBooking] = useState<BookingWithExtras | null>(null);
  const [status, setStatus] = useState<StatusCode>('sitter_confirmed');
  const [startInput, setStartInput] = useState<string>('');
  const [endInput, setEndInput] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Confirm Job Details';
  }, []);

  useEffect(() => {
    const bookingId = Number(pathId);
    if (!bookingId || !user?.id) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(bookingId, { params: { userId: user.id } });
        const b = (resp.data?.data ?? null) as BookingWithExtras | null;
        setBooking(b);
        if (b) {
          setStartInput(toLocalDateTimeInput(b.start));
          setEndInput(toLocalDateTimeInput(b.end));
          setMessage(b.message ?? '');
        }
      } catch (err) {
        console.warn('[BookingConfirmHours] load failed', err);
      }
    };
    void load();
  }, [pathId, user?.id]);

  const isHourly = booking?.paymentType !== 'flat';
  const parentFirstName = booking?.parent?.firstName ?? '';
  const originalDateText = formatDateRange(booking?.originalStart ?? booking?.start, booking?.originalEnd ?? booking?.end);

  const checkDates = (startVal: string, endVal: string): void => {
    const startIso = localDateTimeInputToApi(startVal);
    const endIso = localDateTimeInputToApi(endVal);
    if (!startIso || !endIso) {
      setDateError('');
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      setDateError('End must be after start.');
      return;
    }
    if (new Date(endIso) > new Date()) {
      setDateError('Confirmed hours must be in the past.');
      return;
    }
    setDateError('');
  };

  const exceedsTwelveHours = ((): boolean => {
    if (!booking?.originalStart || !booking?.originalEnd) return false;
    const originalDuration = getDurationHours(booking.originalStart, booking.originalEnd);
    const confirmedStartIso = localDateTimeInputToApi(startInput);
    const confirmedEndIso = localDateTimeInputToApi(endInput);
    if (!confirmedStartIso || !confirmedEndIso) return false;
    const confirmedDuration = getDurationHours(confirmedStartIso, confirmedEndIso);
    return Math.abs(confirmedDuration - originalDuration) > 12;
  })();

  const submit = async (skipModal = false): Promise<void> => {
    if (!booking?.id || !user?.id || isConfirming || dateError) return;
    if (status === 'sitter_confirmed' && isHourly && exceedsTwelveHours && !skipModal) {
      setShowConfirmModal(true);
      return;
    }

    setShowConfirmModal(false);
    setIsConfirming(true);
    try {
      const { statusCode, paymentType } = mapConfirmTypeToPayload(status);
      const patch: Partial<BookingWithExtras> & { paymentType?: 'credit' | 'cash' } = {
        id: booking.id,
        status: { code: statusCode } as Booking['status'],
        message,
      };
      if (paymentType) {
        patch.paymentType = paymentType;
      }
      if (status === 'sitter_confirmed' && isHourly) {
        patch.start = localDateTimeInputToApi(startInput);
        patch.end = localDateTimeInputToApi(endInput);
      }
      await BookingService.patch(booking.id, patch as Booking, {
        params: { userId: user.id },
      });
      navigate(`/bookings/${booking.id}`);
    } catch (err: unknown) {
      setIsConfirming(false);
      const anyErr = err as { response?: { data?: { errors?: Array<{ message?: string }> } } };
      const msgs = (anyErr?.response?.data?.errors ?? []).map((e) => e.message ?? '').filter(Boolean);
      toast.error(msgs.length ? msgs.join(' · ') : t('pages.bookingConfirmHours.submitFailed'));
    }
  };

  if (!booking) {
    return (
      <div className='container py-8'>
        <h2>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          {t('pages.bookingConfirmHours.loading')}
        </h2>
      </div>
    );
  }

  const statusOptions: Array<{ id: string; value: StatusCode; labelKey: string }> = [
    { id: 'status-1', value: 'sitter_confirmed', labelKey: 'pages.bookingConfirmHours.statusExpecting' },
    { id: 'status-2', value: 'cash', labelKey: 'pages.bookingConfirmHours.statusAlreadyPaid' },
    { id: 'status-3', value: 'sitter_cancel', labelKey: 'pages.bookingConfirmHours.statusCanceledBySitter' },
    { id: 'status-4', value: 'parent_cancel', labelKey: 'pages.bookingConfirmHours.statusCanceledByParent' },
  ];

  return (
    <div id='sitter-confirm-details' className='container py-8'>
      <div className='mb-8'>
        <h1 className='text-2xl font-semibold mb-2'>
          {t('pages.bookingConfirmHours.heading', { name: parentFirstName })}
        </h1>
        <p>
          {t('pages.bookingConfirmHours.prompt', { date: originalDateText })}
          <br />
          {t('pages.bookingConfirmHours.tipNote', { name: parentFirstName })}
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
        <div className='md:col-span-5'>
          <div className='us-card rounded p-4 overflow-auto'>
            {!booking.parent?.active ? (
              <span className='float-left mr-4'>
                <img src={DEFAULT_AVATAR} className='w-28 h-28 rounded' alt='' />
              </span>
            ) : (
              <a
                id={`booking-${booking.id}`}
                href={`/app/parent/${booking.parent?.id}`}
                className='float-left mr-4'
              >
                <img
                  alt={booking.parent?.firstName ?? ''}
                  src={booking.parent?.pictureUrl}
                  className='w-28 h-28 rounded'
                />
              </a>
            )}
            <div className='font-medium mt-3 mb-2'>{booking.parent?.name}</div>
            <div>
              <b>
                {getDurationHours(booking.originalStart, booking.originalEnd).toFixed(1)}h:
              </b>{' '}
              <span>{originalDateText}</span>
            </div>
          </div>
        </div>

        <div className='md:col-span-7'>
          <div className='us-card rounded p-5'>
            <h4 className='mb-3 font-semibold'>{t('pages.bookingConfirmHours.statusHeading')}</h4>
            <div className='space-y-2'>
              {statusOptions.map((opt) => (
                <label
                  key={opt.id}
                  htmlFor={opt.id}
                  className='flex items-center gap-2 cursor-pointer'
                >
                  <input
                    id={opt.id}
                    type='radio'
                    name='payment-status'
                    value={opt.value}
                    checked={status === opt.value}
                    onChange={(): void => setStatus(opt.value)}
                  />
                  <span>{t(opt.labelKey)}</span>
                </label>
              ))}
            </div>

            {isHourly && status === 'sitter_confirmed' && (
              <div className='mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='confirm-start'
                    className='block text-sm mb-1 font-semibold'
                  >
                    {t('pages.bookingConfirmHours.startLabel')}
                  </label>
                  <input
                    id='confirm-start'
                    type='datetime-local'
                    value={startInput}
                    onChange={(e): void => {
                      setStartInput(e.target.value);
                      checkDates(e.target.value, endInput);
                    }}
                    className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  />
                </div>
                <div>
                  <label
                    htmlFor='confirm-end'
                    className='block text-sm mb-1 font-semibold'
                  >
                    {t('pages.bookingConfirmHours.endLabel')}
                  </label>
                  <input
                    id='confirm-end'
                    type='datetime-local'
                    value={endInput}
                    onChange={(e): void => {
                      setEndInput(e.target.value);
                      checkDates(startInput, e.target.value);
                    }}
                    className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  />
                </div>
                {dateError && (
                  <div className='sm:col-span-2 text-red-600 text-sm'>{dateError}</div>
                )}
              </div>
            )}

            <div className='mt-8 mb-8'>
              <h4 className='font-semibold mb-2'>
                {t('pages.bookingConfirmHours.messageTo', { name: parentFirstName })}
              </h4>
              <textarea
                id='message'
                maxLength={255}
                rows={3}
                placeholder={t('pages.bookingConfirmHours.messagePlaceholder', {
                  name: parentFirstName,
                })}
                value={message}
                onChange={(e): void => setMessage(e.target.value)}
                className='form-control w-full border border-neutral-300 rounded p-2'
              />
            </div>

            <div className='text-right'>
              <Button
                id='btn-confirm-hours-submit'
                buttonType={ButtonType.primary}
                onClick={(): Promise<void> => submit(false)}
                disabled={isConfirming || !!dateError}
              >
                {isConfirming ? (
                  <>
                    <i className='fa-solid fa-spinner fa-spin mr-2' />
                    {t('pages.bookingConfirmHours.submitting')}
                  </>
                ) : (
                  t('pages.bookingConfirmHours.submit')
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showConfirmModal}
        onClose={(): void => setShowConfirmModal(false)}
        title={t('pages.bookingConfirmHours.modalTitle')}
        className='w-full max-w-md'
      >
        <h4 className='font-semibold mb-2'>{t('pages.bookingConfirmHours.modalSure')}</h4>
        <p className='mb-4'>{t('pages.bookingConfirmHours.modalBody')}</p>
        <div className='px-16 pb-2'>
          <Button
            id='btn-confirm-hours-modal-submit'
            buttonType={ButtonType.primary}
            className='w-full mb-3'
            disabled={isConfirming}
            onClick={(): Promise<void> => submit(true)}
          >
            {isConfirming ? (
              <>
                <i className='fa-solid fa-spinner fa-spin mr-2' />
                {t('pages.bookingConfirmHours.submitting')}
              </>
            ) : (
              t('pages.bookingConfirmHours.modalYes')
            )}
          </Button>
          {!isConfirming && (
            <Button
              id='btn-confirm-hours-modal-cancel'
              buttonType={ButtonType.secondary}
              className='w-full'
              onClick={(): void => setShowConfirmModal(false)}
            >
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
});
